import React from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import { faPlus, faUpload } from '@fortawesome/free-solid-svg-icons'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import FilterLinesIcon from 'client/utils/icons/filter-lines-icon'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  MobileSlider,
  PixelIcon,
  PixelPageContainer,
  Section,
  SectionContent,
} from 'pixel-base'
import queryString from 'query-string'
import { useDebounce } from 'shared/hooks/useDebounce'
import { getCategories, getProducts } from 'utils/api-routes.util'
import { FilterInput } from 'app/common/styles/section'

import BulkProductUpload, {
  BulkProductUploadResult,
} from './components/BulkProductUpload'
import ProductDetail from './components/ProductDetail'
import ProductForm from './components/ProductForm'
import ProductTable from './components/ProductTable'

const breadcrumbs = [
  {
    name: 'All Products',
    active: true,
  },
]

const Product = () => {
  const navigate = useNavigate()
  const { state } = React.useContext(OrdersContext)
  const [products, setProducts] = React.useState<any[]>([])
  const [productId, setProductId] = React.useState<number | null>(null)
  const [editId, setEditId] = React.useState<number | null>(null)
  const [storesList, setStoresList] = React.useState<any>([])
  const [showModal, setShowModal] = React.useState(false)
  const [showUploadSlider, setShowUploadSlider] = React.useState(false)
  const [tableLoading, setTableLoader] = React.useState(true)
  const [categories, setCategories] = React.useState([])
  const [totalRows, setTotalRows] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(0)
  const [searchValue, setSearchValue] = React.useState('')
  const debouncedSearch = useDebounce(searchValue, 500)
  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState<any>({
    page: p.page || 0,
    collection_id: p.collection_id || null,
    store_filter: p.store_filter || 'all', // Default to all stores
    search: p.search || '',
    page_size: p.page_size || 50,
    status: p.status || 'active', // Default to active
  })
  const [showFilters, setShowFilters] = React.useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)
  const [uploadedProducts, setUploadedProducts] = React.useState<
    BulkProductUploadResult[]
  >([])

  // Ref to track if this is the initial load
  const isInitialLoad = React.useRef(true)

  // Ref to prevent multiple simultaneous API calls
  const isFetching = React.useRef(false)

  // Ref to track if page change is from server sync
  const isServerSync = React.useRef(false)

  // Wrapper function to handle edit ID with proper typing
  const handleSetEditId = (id: number | null) => {
    setEditId(id)
  }

  // Helper function to reset pagination when filters change
  const resetPagination = () => {
    setFilters((prev) => ({
      ...prev,
      page: 0,
    }))
  }

  // Helper function to reset all filters to default values
  const resetAllFilters = () => {
    setFilters({
      page: 0,
      collection_id: null,
      store_filter: 'all',
      search: '',
      page_size: 50,
      status: 'active',
    })
    setSearchValue('')
  }

  const onRowClicked = (row) => {
    setShowModal(true)
    setProductId(row.product_id)
  }

  const getAllProducts = () => {
    // Prevent multiple simultaneous API calls
    if (isFetching.current) {
      return
    }

    isFetching.current = true
    setTableLoader(true)

    getProducts(filters).subscribe({
      next: (res: any) => {
        if (res?.response?.data) {
          setProducts(res.response.data)
          setTotalRows(res.response?._meta?.total || 0)
          setTotalPages(res.response?._meta?.total_pages || 0)
        } else {
          setProducts([])
          setTotalRows(0)
          setTotalPages(0)
        }

        // Only sync if there's a significant mismatch to prevent unnecessary triggers
        const serverPage = res?.response?._meta?.page || 1
        const serverPageSize = res?.response?._meta?.page_size || 50

        // Only sync page size if it's different, don't sync page unless there's a real issue
        if (serverPageSize !== filters.page_size) {
          isServerSync.current = true
          setFilters((prev) => ({
            ...prev,
            page_size: serverPageSize,
          }))
          // Reset the flag after a short delay
          setTimeout(() => {
            isServerSync.current = false
          }, 100)
        }

        // This prevents unnecessary resets that trigger more API calls
        if (Math.abs(serverPage - (filters.page + 1)) > 1) {
          isServerSync.current = true
          setFilters((prev) => ({
            ...prev,
            page: serverPage - 1, // Convert 1-based to 0-based
          }))
          // Reset the flag after a short delay
          setTimeout(() => {
            isServerSync.current = false
          }, 100)
        }

        setTableLoader(false)
        isFetching.current = false
      },
      error: () => {
        setTableLoader(false)
        isFetching.current = false
      },
    })
  }

  const fetchProductsCategories = () => {
    getCategories().subscribe((res: any) => {
      setCategories(res.response.data)
    })
  }

  React.useEffect(() => {
    // Only update URL when filters change, but don't fetch data here
    navigate({
      pathname: '/inventory/products',
      search: queryString.stringify(filters),
    })
  }, [filters])

  // Separate effect for data fetching to avoid double triggers
  React.useEffect(() => {
    // Skip the first render to prevent double fetching
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }

    // Skip if this is a server sync to prevent unnecessary API calls
    if (isServerSync.current) {
      return
    }

    getAllProducts()

    // Cleanup function to prevent stale requests
    return () => {
      isFetching.current = false
    }
  }, [
    filters.page,
    filters.page_size,
    filters.collection_id,
    filters.store_filter,
    filters.search,
    filters.status,
  ])

  React.useEffect(() => {
    fetchProductsCategories()
  }, [])

  // Initial data fetch on mount
  React.useEffect(() => {
    getAllProducts()
  }, [])

  // Ensure page size is properly set on mount
  React.useEffect(() => {
    // Force the page size to be 50 if not set
    if (!filters.page_size || filters.page_size !== 50) {
      setFilters((prev) => ({
        ...prev,
        page_size: 50,
      }))
    }
  }, [])

  React.useEffect(() => {
    setStoresList(state?.linkedStores)
  }, [state])

  React.useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearch,
    }))
  }, [debouncedSearch])

  const handlePageChange = (page) => {
    if (filters.page === page - 1 || page < 0) return

    setFilters({
      ...filters,
      page: page - 1,
    })
  }

  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
      page: 0, // Reset to first page when page size changes
    })
  }

  const options = [
    {
      collection_name: 'All Collections',
      id: null,
    },
    ...categories,
  ].map((item: any) => {
    // Handle "All Collections" option which has a different structure
    if (item.collection_name === 'All Collections') {
      return {
        label: item.collection_name,
        value: item.id?.toString() || '',
      }
    }
    // Handle categories from API which have nested collection_id structure
    return {
      label: item?.collection_id?.source_collection_name,
      value: item?.collection_id?.id?.toString(),
    }
  })

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Draft', value: 'draft' },
    { label: 'Archived', value: 'archived' },
  ]

  const storeOptions = [
    { label: 'All Stores', value: 'all' },
    ...storesList
      .filter((store: any) => store.store_type !== 'ems' && store.store_id)
      .map((store: any) => ({
        label: store.store_name,
        value: store.store_id.toString(),
      })),
    { label: 'No Store Linked', value: 'none' },
  ]

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
        >
          <DcBreadcrumbs
            className="breadcrumbs"
            fontSize="16px"
            data={breadcrumbs}
          />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px" height="calc(100vh - 61px)">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px' }}>
            <DcFlexBox
              height="auto"
              padding="0px 20px"
              justifyContent="space-between"
              alignItems="flex-end"
            >
              {!isMobile ? (
                <DcFlexBox
                  padding="5px 0px"
                  width="100%"
                  gap="5px"
                  justifyContent="flex-end"
                >
                  <FilterInput>
                    <DcInput
                      inputSize="small"
                      showsearchicon={0}
                      placeholder="Search"
                      name="search"
                      onChange={(e) => {
                        setResetPaginationToggle(e.target.value.length > 0)
                        setSearchValue(e.target.value)
                        resetPagination()
                      }}
                      value={searchValue}
                    />
                  </FilterInput>
                  <FilterInput style={{ width: '15%' }}>
                    <DcDropDown
                      dropdownSize="sm"
                      placeholder="Status"
                      customLabel=""
                      options={statusOptions}
                      value={filters.status}
                      onChange={(e) => {
                        resetPagination()
                        setFilters({
                          ...filters,
                          status: e.target.value,
                        })
                      }}
                    />
                  </FilterInput>
                  <FilterInput style={{ width: '20%' }}>
                    <DcDropDown
                      dropdownSize="sm"
                      placeholder="Stores"
                      customLabel=""
                      options={storeOptions}
                      value={filters.store_filter}
                      onChange={(e) => {
                        resetPagination()
                        setFilters({
                          ...filters,
                          store_filter: e.target.value,
                        })
                      }}
                    />
                  </FilterInput>
                  <FilterInput style={{ width: '20%' }}>
                    <DcDropDown
                      dropdownSize="sm"
                      placeholder="Product Collections"
                      customLabel=""
                      options={options}
                      value={filters.collection_id}
                      onChange={(e) => {
                        resetPagination()
                        setFilters({
                          ...filters,
                          collection_id: e.target.value,
                        })
                      }}
                    />
                  </FilterInput>
                  <DcButton
                    size="sm"
                    variant="secondary"
                    onClick={resetAllFilters}
                  >
                    Reset Filters
                  </DcButton>
                  <DcButton
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowUploadSlider(true)}
                  >
                    <PixelIcon color="white" icon={faUpload} />
                    &nbsp;Upload Products
                  </DcButton>
                  <DcButton
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      setShowModal(true)
                      setProductId(null)
                      setEditId(null)
                    }}
                  >
                    <PixelIcon color="white" icon={faPlus} />
                    &nbsp;Add Product
                  </DcButton>
                </DcFlexBox>
              ) : (
                <DcFlexBox
                  position="relative"
                  height="auto"
                  gap="5px"
                  padding="5px 0px"
                >
                  <DcInput
                    showsearchicon={0}
                    placeholder="Search"
                    name="search"
                    onChange={(e) => {
                      setResetPaginationToggle(e.target.value.length > 0)
                      setSearchValue(e.target.value)
                      resetPagination()
                    }}
                    value={searchValue}
                  />
                  <DcDiv position="absolute" right="0px" top="2px">
                    <DcButton
                      variant="link"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FilterLinesIcon size={18} />
                    </DcButton>
                  </DcDiv>
                </DcFlexBox>
              )}
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent>
            <ProductTable
              key={`products-table-${filters.page_size}`} // Force re-render when page size changes
              products={products || []}
              loading={tableLoading}
              totalRows={totalRows}
              pageSize={filters.page_size}
              currentPage={filters.page + 1}
              onPageChange={handlePageChange}
              onPerRowsChange={handlePerRowsChange}
              onRowClick={onRowClicked}
              resetPaginationToggle={resetPaginationToggle}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      <MobileSlider
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditId(null)
          setProductId(null)
        }}
        title={productId !== null ? 'Product Details' : 'Add Product'}
        width="70%"
        padding="0px"
      >
        {productId !== null ? (
          editId !== null ? (
            <ProductForm
              productId={editId}
              setEditId={handleSetEditId}
              setProductId={setProductId}
            />
          ) : (
            <ProductDetail productId={productId} setEditId={handleSetEditId} />
          )
        ) : (
          <ProductForm />
        )}
      </MobileSlider>

      <MobileSlider
        isOpen={showFilters}
        onClose={() => {
          setShowFilters(false)
        }}
        title="Filters"
        customWith="70%"
        padding="0px"
      >
        <DcFlexBox
          padding="5px 10px"
          width="100%"
          gap="5px"
          flexDirection="column"
        >
          <DcDropDown
            dropdownSize="sm"
            placeholder="Status"
            customLabel=""
            options={statusOptions}
            value={filters.status}
            onChange={(e) => {
              resetPagination()
              setFilters({
                ...filters,
                status: e.target.value,
              })
            }}
          />

          <DcDropDown
            dropdownSize="sm"
            placeholder="Stores"
            customLabel=""
            options={storeOptions}
            value={filters.store_filter}
            onChange={(e) => {
              resetPagination()
              setFilters({
                ...filters,
                store_filter: e.target.value,
              })
            }}
          />
          <DcDropDown
            dropdownSize="sm"
            placeholder="Product Collections"
            customLabel=""
            options={options}
            value={filters.collection_id}
            onChange={(e) => {
              resetPagination()
              setFilters({
                ...filters,
                collection_id: e.target.value,
              })
            }}
          />
          <DcButton size="sm" variant="secondary" onClick={resetAllFilters}>
            Reset Filters
          </DcButton>
          <DcButton
            size="sm"
            variant="secondary"
            onClick={() => {
              setShowFilters(false)
              setShowUploadSlider(true)
            }}
          >
            <PixelIcon color="white" icon={faUpload} />
            &nbsp;Upload Products
          </DcButton>
          <DcButton
            size="sm"
            variant="primary"
            onClick={() => {
              setShowModal(true)
              setProductId(null)
              setEditId(null)
            }}
          >
            <PixelIcon color="white" icon={faPlus} />
            &nbsp;Add Product
          </DcButton>
        </DcFlexBox>
      </MobileSlider>

      <DcOverlaySlider
        isOpen={showUploadSlider}
        onClose={() => {
          setShowUploadSlider(false)
        }}
        title="Upload Products"
        width="40%"
        padding="0px"
      >
        <BulkProductUpload
          parsedProducts={uploadedProducts}
          onProductsParsed={(products) => setUploadedProducts(products)}
          onClose={() => setShowUploadSlider(false)}
        />
      </DcOverlaySlider>
    </>
  )
}

export default Product
